<template>
  <v-container fluid>
    <v-card outlined elevation="1">
      <v-toolbar color="primary" dark dense flat>
        <v-toolbar-title>Create Downtime Report</v-toolbar-title>
        <v-spacer></v-spacer>

        <!-- Back Button -->
        <v-btn color="white" small text :to="'/downtime-reports'">
          <v-icon left>mdi-arrow-left</v-icon>
          Back
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <form ref="mainFormRef">
          <v-row dense>
            <!-- Service Report -->
            <v-col cols="12">
              <v-switch inset color="primary" label="Service Report" v-model="isServiceReport" />
            </v-col>

            <!-- Customer -->
            <v-col cols="12" md="6">
              <v-autocomplete
                outlined
                dense
                hide-details="auto"
                label="Customer"
                name="customer_id"
                item-value="id"
                :item-text="(item) => `${item.id} - ${item.name}`"
                :items="customers"
                :error-messages="errors.customer_id"
              />
            </v-col>

            <!-- Unit -->
            <v-col cols="12" md="6">
              <v-text-field
                outlined
                dense
                hide-details="auto"
                label="Unit"
                name="unit_id"
                :error-messages="errors.unit_id"
              />
            </v-col>

            <!-- Subject -->
            <v-col cols="12" md="6" v-if="isServiceReport">
              <v-text-field
                outlined
                hide-details="auto"
                dense
                label="Subject"
                name="subject"
                :value="subject"
                :error-messages="errors.subject"
              />
            </v-col>
            <!-- Machine Type -->
            <v-col cols="12" md="6">
              <v-select
                outlined
                dense
                hide-details="auto"
                label="Machine Type"
                name="machine_type_id"
                :items="machineTypes"
                item-value="id"
                item-text="name"
                v-model="machineTypeId"
                :error-messages="errors.machine_type_id"
              />
            </v-col>
            <!-- Reported At -->
            <v-col cols="12" md="6">
              <v-text-field
                outlined
                dense
                hide-details="auto"
                label="Reported At"
                name="reported_at"
                type="datetime-local"
                :value="reportedAt"
                :error-messages="errors.reported_at"
              />
            </v-col>
            <!-- Dates -->
            <v-col cols="12" md="6">
              <v-text-field
                outlined
                dense
                hide-details="auto"
                label="Date Start"
                name="date_start"
                type="datetime-local"
                :value="dateStart"
                :error-messages="errors.date_start"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                outlined
                dense
                hide-details="auto"
                label="Date End"
                name="date_end"
                type="datetime-local"
                :error-messages="errors.date_end"
              />
            </v-col>

            <!-- Status -->
            <v-col cols="12" md="6">
              <v-select
                outlined
                dense
                hide-details="auto"
                label="Status Before"
                name="list_status_before"
                :items="['operational', 'non-operational']"
                :error-messages="errors.list_status_before"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                outlined
                dense
                hide-details="auto"
                label="Status After"
                name="list_status_after"
                :items="['operational', 'non-operational']"
                :error-messages="errors.list_status_after"
              />
            </v-col>

            <!-- Issue / Error -->
            <v-col cols="12" md="6" v-if="!isServiceReport">
              <v-text-field
                outlined
                dense
                hide-details="auto"
                label="Reported Issue"
                name="reported_issue"
                :error-messages="errors.reported_issue"
              />
            </v-col>

            <!-- Description -->
            <v-col cols="12" v-if="isServiceReport">
              <v-textarea
                outlined
                hide-details="auto"
                dense
                rows="3"
                auto-grow
                label="Description"
                name="description"
                :value="description"
                :error-messages="errors.description"
              />
            </v-col>
            <v-col cols="12" md="6" v-if="!isServiceReport">
              <v-text-field
                outlined
                dense
                hide-details="auto"
                label="Error Code"
                name="error"
                :error-messages="errors.error"
              />
            </v-col>
          </v-row>

          <!-- Inspection Findings -->
          <v-divider class="my-4"></v-divider>
          <h4 class="text-subtitle-1 font-weight-medium mb-2">Inspection Findings</h4>
          <v-row dense>
            <v-col cols="12" v-for="(finding, i) in inspectionFindings" :key="'finding-' + i">
              <v-text-field
                outlined
                dense
                hide-details="auto"
                :label="`Finding ${i + 1}`"
                v-model="inspectionFindings[i]"
                append-icon="mdi-delete"
                @click:append="inspectionFindings.splice(i, 1)"
              />
            </v-col>
            <v-col cols="12">
              <v-btn small color="green" @click="inspectionFindings.push('')">
                <v-icon left small>mdi-plus</v-icon>Add Finding
              </v-btn>
            </v-col>
          </v-row>

          <!-- Actions -->
          <v-divider class="my-4"></v-divider>
          <h4 class="text-subtitle-1 font-weight-medium mb-2">Actions</h4>
          <v-row dense>
            <v-col cols="12" v-for="(action, i) in actions" :key="'action-' + i">
              <v-text-field
                outlined
                dense
                hide-details="auto"
                :label="`Action ${i + 1}`"
                v-model="actions[i]"
                append-icon="mdi-delete"
                :error-messages="errors[`actions.${i}.action`]"
                @click:append="removeAction(i)"
              />
            </v-col>
            <v-col cols="12">
              <v-btn small color="green" @click="actions.push('')">
                <v-icon left small>mdi-plus</v-icon>Add Action
              </v-btn>
            </v-col>
          </v-row>

          <!-- Recommendations -->
          <v-divider class="my-4"></v-divider>
          <h4 class="text-subtitle-1 font-weight-medium mb-2">Recommendations</h4>
          <v-row dense>
            <v-col cols="12" v-for="(rec, i) in recommendations" :key="'rec-' + i">
              <v-text-field
                outlined
                dense
                hide-details="auto"
                :label="`Recommendation ${i + 1}`"
                v-model="recommendations[i]"
                append-icon="mdi-delete"
                @click:append="recommendations.splice(i, 1)"
              />
            </v-col>
            <v-col cols="12">
              <v-btn small color="green" @click="recommendations.push('')">
                <v-icon left small>mdi-plus</v-icon>Add Recommendation
              </v-btn>
            </v-col>
          </v-row>
          <!-- Purchase Needed -->
          <v-divider class="my-4"></v-divider>
          <v-switch
            inset
            label="Purchase Needed"
            v-model="purchaseNeeded"
            :true-value="true"
            :false-value="false"
          ></v-switch>
          <!-- IMAGES -->
          <v-divider class="my-4"></v-divider>

          <h4 class="text-subtitle-1 font-weight-medium mb-2">Images</h4>

          <v-row dense v-for="(row, rowIndex) in imageRows" :key="'row-' + rowIndex" align="center">
            <v-col
              v-for="(img, i) in row"
              :key="'img-' + rowIndex + '-' + i"
              :cols="12"
              :sm="Math.floor(spanToNumber(img.span) * 12)"
            >
              <div class="d-flex flex-column" style="gap: 10px">
                <!-- IMAGE NAME -->
                <v-text-field
                  outlined
                  dense
                  hide-details="auto"
                  label="Image Name"
                  v-model="img.name"
                  :error-messages="errors[`images.${getImageIndex(img)}.name`]"
                />

                <!-- FILE -->
                <v-file-input
                  outlined
                  dense
                  hide-details="auto"
                  accept="image/*"
                  prepend-inner-icon="mdi-image"
                  append-icon=""
                  label="Image"
                  @change="onFileChange($event, img)"
                  :error-messages="errors[`images.${getImageIndex(img)}.file`]"
                />

                <!-- SPAN -->
                <v-select
                  outlined
                  dense
                  hide-details="auto"
                  label="Span"
                  :items="spanOptions"
                  item-text="label"
                  item-value="value"
                  v-model="img.span"
                  :error-messages="errors[`images.${getImageIndex(img)}.span`]"
                />
              </div>

              <!-- PREVIEW -->
              <v-img
                v-if="img.preview"
                :src="img.preview"
                max-height="220"
                class="mt-2 rounded"
                contain
              />

              <!-- REMOVE -->
              <v-btn icon small color="red" class="mt-1" @click="removeImage(img)">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>

          <v-btn small color="blue darken-1" dark class="mt-2" @click="addImage">
            <v-icon left>mdi-plus</v-icon>
            Add Image
          </v-btn>
          <!-- Prepared By -->
          <v-divider class="my-4"></v-divider>
          <h4 class="text-subtitle-1 font-weight-medium mb-2">Prepared By</h4>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-combobox
                outlined
                dense
                hide-details="auto"
                label="Prepared By"
                v-model="preparedByObject"
                :items="preparedByDropdown"
                :error-messages="errors.prepared_by"
                :search-input.sync="preparedBySearch"
                no-filter
                return-object
                item-value="prepared_by"
              >
                <template #selection="{ item }">{{ item.prepared_by }}</template>
                <template #item="{ item }"
                  >{{ item.prepared_by }}&nbsp;-&nbsp;<small>{{
                    item.prepared_by_position || 'N/A'
                  }}</small>
                </template>
              </v-combobox>
            </v-col>

            <v-col cols="12" md="6">
              <v-combobox
                outlined
                dense
                hide-details="auto"
                label="Position"
                :error-messages="errors.prepared_by_position"
                :items="preparedByPositions"
                v-model="preparedByPosition"
              />
            </v-col>
          </v-row>
          <!-- Buttons -->
          <v-row dense class="mt-3">
            <v-col cols="12" sm="4">
              <v-btn block :disabled="!purchaseNeeded" @click="components.open = true">
                <v-icon left>mdi-cog</v-icon> Components
              </v-btn>
            </v-col>

            <v-col cols="12" sm="4">
              <v-btn block color="primary" dark @click="store">
                <v-icon left>mdi-check</v-icon> Submit
              </v-btn>
            </v-col>
          </v-row>
        </form>
      </v-card-text>
    </v-card>

    <!-- Components Dialog -->
    <v-dialog v-model="components.open" max-width="600" persistent>
      <v-card>
        <v-card-title>Components</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" v-for="(component, i) in components.data" :key="i">
              <v-autocomplete
                outlined
                dense
                hide-details="auto"
                :label="`Component ${i + 1}`"
                v-model="components.data[i]"
                item-value="id"
                :item-text="(item) => `(${item.typeName} - ${item.categoryName}) ${item.name}`"
                :items="filteredMachineComponents"
                :error-messages="errors[`machine_components.${i}.machine_component_id`]"
                append-icon="mdi-delete"
                @click:append="removeComponent(i)"
              />
            </v-col>
          </v-row>
          <div class="d-flex justify-space-between mt-2">
            <v-btn small color="green" @click="components.data.push(null)">
              <v-icon left small>mdi-plus</v-icon>Add
            </v-btn>
            <v-btn small color="red" @click="components.open = false">
              <v-icon left small>mdi-close</v-icon>Close
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import appAxios from '@/libs/app-axios'
import { ErrorPopup, LoadingPopup, SuccessPopup, toISOString } from '@/libs/utils'
import { isAxiosError } from 'axios'
import _ from 'lodash'

export default {
  name: 'CreateDowntimeReport',
  data() {
    return {
      spanOptions: [
        { label: '1/3', value: '1/3' },
        { label: '1/2', value: '1/2' },
        { label: '1', value: '1' },
      ],
      images: [],

      subject: '',
      description: '',
      dateStart: toISOString(new Date()),
      errors: {},
      customers: [],
      machineTypes: [],
      machineComponents: [],
      machineComponentCategories: [],
      machineTypeId: null,
      purchaseNeeded: false,
      isServiceReport: false, // ✅ NEW
      preparedByObject: {},
      preparedBy: '',
      preparedByPosition: '',
      inspectionFindings: [],
      recommendations: [],
      actions: [],
      preparedByDropdown: [],
      preparedByPositions: [],
      preparedBySearch: '',
      components: { open: false, data: [] },
      debouncedFetchOptions: null,
    }
  },
  created() {
    this.fetchOptions()
    this.fetchCustomers()
    this.fetchPositions()
    this.debouncedFetchOptions = _.debounce(this.fetchPreparedByDropdown, 300)
  },
  watch: {
    preparedBySearch() {
      this.debouncedFetchOptions()
    },
    preparedByObject(newVal) {
      if (newVal) {
        this.preparedBy = newVal.prepared_by
        this.preparedByPosition = newVal.prepared_by_position
        return
      }
      this.preparedBy = ''
      this.preparedByPosition = ''
    },
  },
  computed: {
    mapMachineCategories() {
      return this.machineComponentCategories.reduce((map, c) => {
        map[c.id] = c.name
        return map
      }, {})
    },

    mapMachineTypes() {
      return this.machineTypes.reduce((map, t) => {
        map[t.id] = t.name
        return map
      }, {})
    },

    filteredMachineComponents() {
      return this.machineComponents.reduce((arr, comp) => {
        const categoryName = this.mapMachineCategories[comp.machine_component_category_id]

        const typeName = this.mapMachineTypes[comp.machine_type_id]

        if (!this.machineTypeId || comp.machine_type_id === this.machineTypeId) {
          arr.push({
            ...comp,
            categoryName,
            typeName,
          })
        }

        return arr
      }, [])
    },

    imageRows() {
      const rows = []
      let currentRow = []
      let currentSpan = 0

      this.images.forEach((img) => {
        const span = this.spanToNumber(img.span)

        if (currentSpan + span > 1) {
          rows.push(currentRow)
          currentRow = []
          currentSpan = 0
        }

        currentRow.push(img)
        currentSpan += span
      })

      if (currentRow.length) {
        rows.push(currentRow)
      }

      return rows
    },
  },
  methods: {
    spanToNumber(span) {
      if (span === '1') return 1
      if (span === '1/2') return 0.5
      if (span === '1/3') return 1 / 3

      return 0.5
    },
    addImage() {
      this.images.push({
        name: '',
        span: '1/2',
        file: null,
        preview: null,
      })
    },

    removeImage(img) {
      const index = this.images.indexOf(img)

      if (index > -1) {
        this.images.splice(index, 1)
      }
    },

    onFileChange(file, img) {
      img.file = file
      img.preview = file ? URL.createObjectURL(file) : null
    },

    getImageIndex(img) {
      return this.images.indexOf(img)
    },
    async fetchPositions() {
      try {
        const axios = appAxios()
        const { data } = await axios.get('/v3/downtime-reports/prepared-by/positions')
        this.preparedByPositions = data.data || []
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching prepared by positions:', e)
        }
        if (isAxiosError(e)) {
          console.error('Failed to fetch prepared by dropdown:', e.response || e.message)
        }
      }
    },
    async fetchOptions() {
      const axios = appAxios()
      const { data } = await axios.get('/v3/machine-components/options')
      this.machineComponents = data?.components || []
      this.machineTypes = data?.types || []
      this.machineComponentCategories = data?.categories || []
    },
    async fetchCustomers() {
      const axios = appAxios()
      const { data } = await axios.get('/v3/customers/dropdown-options')
      this.customers = data.data || []
    },
    removeAction(i) {
      this.actions.splice(i, 1)
    },
    removeComponent(i) {
      this.components.data.splice(i, 1)
    },
    reset() {
      const form = /** @type {HTMLFormElement | null} */ (this.$refs.mainFormRef)
      if (form) form.reset()
      this.inspectionFindings = []
      this.recommendations = []
      this.actions = []
      this.components.data = []
    },
    async fetchPreparedByDropdown() {
      try {
        const axios = appAxios()
        const { data } = await axios.get('/v3/downtime-reports/prepared-by/dropdown', {
          params: { search: this.preparedBySearch },
        })
        this.preparedByDropdown = data.data
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching prepared by dropdown:', error)
        }
        if (isAxiosError(error)) {
          console.error('Failed to fetch prepared by dropdown:', error.response || error.message)
        }
      }
    },
    async store() {
      try {
        LoadingPopup.fire('Loading', 'Creating downtime report...')
        this.errors = {}
        const form = /** @type {HTMLFormElement|null} */ (this.$refs.mainFormRef)
        const formData = new FormData(form)
        // @ts-ignore
        // const payload = Object.fromEntries(formData.entries())

        formData.set(
          'inspection_findings',
          this.inspectionFindings.filter((item) => item).join('\n')
        )
        formData.set('recommendations', this.recommendations.filter((item) => item).join('\n'))
        formData.set('prepared_by', this.preparedBy)
        formData.set('prepared_by_position', this.preparedByPosition)
        formData.set('purchase_needed', this.purchaseNeeded ? '1' : '0')
        formData.set('is_service_report', this.isServiceReport ? '1' : '0')

        this.actions.forEach((action, i) => {
          formData.append(`actions[${i}][action]`, action)
        })

        this.components.data.forEach((cId, i) => {
          formData.append(`machine_components[${i}][machine_component_id]`, cId)
        })

        this.images.forEach((img, i) => {
          formData.append(`images[${i}][name]`, img.name ?? '')

          formData.append(`images[${i}][span]`, img.span ?? '1/2')

          if (img.file) {
            formData.append(`images[${i}][file]`, img.file)
          }
        })
        const axios = appAxios()
        const { data } = await axios.post('/v3/downtime-reports', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        this.reset()
        this.$router.push({
          name: 'DowntimeReports.Edit',
          params: { downtimeReportId: data.downtime_report_id },
        })
        SuccessPopup.fire('Success', 'Downtime report created successfully!')
      } catch (error) {
        LoadingPopup.close()
        if (isAxiosError(error) && error.response?.status === 422) {
          this.errors = error.response.data.errors || {}
          return
        }
        ErrorPopup.fire('Failed to create downtime report.', error)
      }
    },
  },
}
</script>
